import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import MailIcon from '@mui/icons-material/MailOutline';
import LockResetIcon from '@mui/icons-material/LockReset';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import { apiFetch } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { SectionLabel, PanelCard } from '../components/primitives';
import { useAuth } from '../lib/auth';
import { formatShort } from '../lib/dates';

/**
 * Staff.
 *
 * The record plus its Supabase Auth account. There is no password field
 * anywhere on this screen and there never should be: the legacy app stored
 * `hashedpassword` and `salt` on the staff row and hashed the raw password on
 * save, and none of that comes across. Accounts are created by invitation and
 * passwords are set by the person they belong to.
 *
 * Laid out like the Jobs page (click2gro General Tasks): full-width container,
 * filter bar in a card, striped rows with client-side pagination, and the
 * record opened in a dialog.
 */

type AuthStatus = 'no_account' | 'invited' | 'active' | 'revoked';

interface StaffMember {
    readonly staffId: number;
    readonly authUserId: string | null;
    readonly staffName: string;
    readonly emailAddress: string | null;
    readonly phone: string | null;
    readonly staffGroupId: number | null;
    readonly staffTitleId: number | null;
    readonly isActive: boolean;
    readonly employmentType: string | null;
    readonly payFrequency: string | null;
    readonly hoursPerWeek: number | null;
    readonly baseHours: number | null;
    readonly standardHours: number | null;
    readonly rdoAccrual: number | null;
    readonly startDate: string | null;
    readonly showOnTimesheet: boolean;
    /** Optional: older API responses and the preview omit them. */
    readonly mobileCalendarShowAll?: boolean;
    readonly mobileContractorView?: boolean;
    readonly showOnCalendar?: boolean;
    readonly invitedAt: string | null;
    readonly accessRevokedAt: string | null;
    readonly lastLoginAt: string | null;
    readonly authStatus: AuthStatus;
    /** payroll_category ids. Optional: older API responses and the preview omit it. */
    readonly payrollCategoryIds?: readonly number[];
}

type DisplayFlag = 'showOnTimesheet' | 'mobileCalendarShowAll' | 'mobileContractorView' | 'showOnCalendar';

/**
 * The three checkboxes from the legacy form's status row, labelled as they
 * were there so the office recognises them. "Do not show timesheet" is stored
 * the other way round, as show_on_timesheet, hence `inverted`.
 */
const DISPLAY_FLAGS: readonly { key: DisplayFlag; label: string; hint: string; inverted?: boolean }[] = [
    {
        key: 'showOnTimesheet',
        label: 'Do not show timesheet',
        hint: 'Leaves them off the Timesheet screen and payroll.',
        inverted: true,
    },
    {
        key: 'mobileCalendarShowAll',
        label: 'Show complete Calendar on Mobile View',
        hint: "On a phone, lists everyone's jobs for the day, not only their own.",
    },
    {
        key: 'mobileContractorView',
        label: 'Show contractor on calendar mobile view',
        hint: 'On a phone, adds a Calendar | Timesheet switch above their job list.',
    },
    {
        key: 'showOnCalendar',
        label: 'Do not show as staff on calendar',
        hint: "Leaves them out of the calendar's staff list, so they can't be put on a job.",
        inverted: true,
    },
];

interface PayrollCategory {
    readonly id: number;
    readonly name: string;
    readonly kind: string;
}

interface Skillset {
    readonly id: number;
    readonly title: string;
    readonly colour: string | null;
}

const AUTH_LABEL: Record<AuthStatus, { label: string; colour: 'default' | 'success' | 'warning' | 'error' }> = {
    no_account: { label: 'No account', colour: 'default' },
    invited: { label: 'Invited', colour: 'warning' },
    active: { label: 'Active', colour: 'success' },
    revoked: { label: 'Revoked', colour: 'error' },
};

const STATUS_FILTERS = [
    { value: 'active', label: 'Active' },
    { value: 'all', label: 'All' },
    { value: 'disabled', label: 'Disabled' },
] as const;

type StatusFilter = (typeof STATUS_FILTERS)[number]['value'];

export function Staff(): React.JSX.Element {
    const { me } = useAuth();
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState<StatusFilter>('active');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [staff, setStaff] = useState<readonly StaffMember[]>([]);
    const [groups, setGroups] = useState<readonly { id: number; name: string }[]>([]);
    const [skillsets, setSkillsets] = useState<readonly Skillset[]>([]);
    const [payrollCategories, setPayrollCategories] = useState<readonly PayrollCategory[]>([]);
    const [selected, setSelected] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [nonce, setNonce] = useState(0);
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([
            apiFetch<{ groups: readonly { id: number; name: string }[] }>('/staff-admin/groups'),
            apiFetch<{ skillsets: readonly Skillset[] }>('/skillsets'),
        ])
            .then(([g, s]) => {
                setGroups(g.groups);
                setSkillsets(s.skillsets);
            })
            .catch(() => {
                /* The pickers stay empty; the list still loads. */
            });
        apiFetch<{ categories: readonly PayrollCategory[] }>('/staff-admin/payroll-categories')
            .then((r) => {
                setPayrollCategories(r.categories);
            })
            .catch(() => {
                /* The pickers stay empty; the list still loads. */
            });
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const timer = setTimeout(
            () => {
                const params = new URLSearchParams();
                if (search.trim() !== '') {
                    params.set('search', search.trim());
                }
                params.set('status', status);
                apiFetch<{ staff: readonly StaffMember[] }>(`/staff-admin?${params.toString()}`)
                    .then((r) => {
                        if (!cancelled) {
                            setStaff(r.staff);
                            setError(null);
                        }
                    })
                    .catch((err: unknown) => {
                        if (!cancelled) {
                            setError(err instanceof Error ? err.message : 'Could not load staff');
                        }
                    })
                    .finally(() => {
                        if (!cancelled) {
                            setLoading(false);
                        }
                    });
            },
            search.trim() === '' ? 0 : 250,
        );
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [search, status, nonce]);

    const reload = useCallback(() => {
        setNonce((n) => n + 1);
    }, []);

    const current = useMemo(
        () => staff.find((s) => s.staffId === selected) ?? null,
        [staff, selected],
    );

    const pendingInvites = useMemo(
        () => staff.filter((s) => s.authStatus === 'no_account').length,
        [staff],
    );

    function closeCreate(): void {
        setCreating(false);
        setNewName('');
        setNewEmail('');
    }

    function create(): void {
        setSaving(true);
        apiFetch<StaffMember>('/staff-admin', {
            method: 'POST',
            body: JSON.stringify({ staffName: newName, emailAddress: newEmail === '' ? null : newEmail }),
        })
            .then((s) => {
                closeCreate();
                reload();
                setSelected(s.staffId);
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not create the staff member');
            })
            .finally(() => {
                setSaving(false);
            });
    }

    // Clamp rather than reset in an effect: a search that shrinks the list
    // should land on its last page, not an empty one.
    const lastPage = Math.max(0, Math.ceil(staff.length / rowsPerPage) - 1);
    const currentPage = Math.min(page, lastPage);
    const pageRows = staff.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <PageHeader title="Staff" subtitle="Staff records and sign-in accounts" />

            <Paper sx={{ p: 2, mb: 3 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    flexWrap="wrap"
                >
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        flexWrap="wrap"
                        sx={{ flex: 1 }}
                    >
                        <TextField
                            size="small"
                            placeholder="Search name or email..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(0);
                            }}
                            slotProps={{
                                input: { startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> },
                            }}
                            sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 360 } }}
                        />
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                            {STATUS_FILTERS.map((f) => (
                                <Button
                                    key={f.value}
                                    size="small"
                                    variant={status === f.value ? 'contained' : 'outlined'}
                                    color={status === f.value ? 'primary' : 'inherit'}
                                    onClick={() => {
                                        setStatus(f.value);
                                        setPage(0);
                                    }}
                                    sx={{ flex: { xs: '1 1 30%', sm: '0 0 auto' } }}
                                >
                                    {f.label}
                                    {status === f.value && !loading ? ` (${String(staff.length)})` : ''}
                                </Button>
                            ))}
                            {pendingInvites > 0 ? (
                                <Tooltip title="Staff who haven't been invited yet — they can't sign in until they accept an invite">
                                    <Chip
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        icon={<MailIcon sx={{ fontSize: 14 }} />}
                                        label={`${String(pendingInvites)} to invite`}
                                    />
                                </Tooltip>
                            ) : null}
                            {loading ? <CircularProgress size={18} /> : null}
                        </Stack>
                    </Stack>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setCreating(true);
                        }}
                    >
                        New Staff
                    </Button>
                </Stack>
            </Paper>

            {error !== null ? (
                <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={reload}>Retry</Button>}>
                    {error}
                </Alert>
            ) : null}
            {notice !== null ? (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    onClose={() => {
                        setNotice(null);
                    }}
                >
                    {notice}
                </Alert>
            ) : null}

            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 720 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Skillset</TableCell>
                            <TableCell>Sign-in</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                    <Typography color="text.secondary">
                                        {loading
                                            ? 'Loading staff...'
                                            : search.trim() === ''
                                              ? status === 'disabled'
                                                  ? 'No disabled staff.'
                                                  : 'No staff here.'
                                              : `No staff match "${search}".`}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            pageRows.map((s, idx) => {
                                const badge = AUTH_LABEL[s.authStatus];
                                return (
                                    <TableRow
                                        key={s.staffId}
                                        hover
                                        onClick={() => {
                                            setSelected(s.staffId);
                                        }}
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor: idx % 2 !== 0 ? 'action.hover' : 'transparent',
                                            opacity: s.isActive ? 1 : 0.55,
                                        }}
                                    >
                                        <TableCell sx={{ fontWeight: 600 }}>{s.staffName}</TableCell>
                                        <TableCell>{s.emailAddress ?? '—'}</TableCell>
                                        <TableCell>
                                            {groups.find((g) => g.id === s.staffGroupId)?.name ?? '—'}
                                        </TableCell>
                                        <TableCell>
                                            {skillsets.find((k) => k.id === s.staffTitleId)?.title ?? '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" variant="outlined" color={badge.colour} label={badge.label} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelected(s.staffId);
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={staff.length}
                page={currentPage}
                onPageChange={(_e, p) => {
                    setPage(p);
                }}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(Number.parseInt(e.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ bgcolor: 'background.paper', borderRadius: 1, mt: 1 }}
            />

            <Dialog
                open={current !== null}
                onClose={() => {
                    setSelected(null);
                }}
                maxWidth="md"
                fullWidth
                slotProps={{ paper: { elevation: 1 } }}
            >
                {current !== null ? (
                    <StaffDetail
                        member={current}
                        groups={groups}
                        skillsets={skillsets}
                        payrollCategories={payrollCategories}
                        isSelf={me?.staffId === current.staffId}
                        onChanged={reload}
                        onSaved={() => {
                            setNotice(`${current.staffName} updated`);
                            setSelected(null);
                            reload();
                        }}
                        onDisabledChanged={(message) => {
                            setNotice(message);
                            setSelected(null);
                            reload();
                        }}
                        onClose={() => {
                            setSelected(null);
                        }}
                    />
                ) : null}
            </Dialog>

            <Dialog
                open={creating}
                onClose={closeCreate}
                maxWidth="sm"
                fullWidth
                slotProps={{ paper: { elevation: 1 } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Add Staff
                    <IconButton size="small" aria-label="Close" disabled={saving} onClick={closeCreate}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack gap={1.5}>
                        <TextField
                            label="Name"
                            size="small"
                            fullWidth
                            required
                            autoFocus
                            value={newName}
                            onChange={(e) => {
                                setNewName(e.target.value);
                            }}
                        />
                        <TextField
                            label="Email"
                            size="small"
                            fullWidth
                            value={newEmail}
                            onChange={(e) => {
                                setNewEmail(e.target.value);
                            }}
                            helperText="Where their invite goes. Can be added later."
                        />
                        <Alert severity="info" sx={{ fontSize: '0.78rem' }}>
                            No password is set here. The account is created when you send the invite,
                            and they choose their own password.
                        </Alert>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={closeCreate} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        onClick={create}
                        disabled={saving || newName.trim() === ''}
                    >
                        {saving ? 'Saving...' : 'Save Staff'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

// ─── Detail ─────────────────────────────────────────────────────────────────

interface StaffDetailProps {
    readonly member: StaffMember;
    readonly groups: readonly { id: number; name: string }[];
    readonly skillsets: readonly Skillset[];
    readonly payrollCategories: readonly PayrollCategory[];
    readonly isSelf: boolean;
    /** Sign-in actions: the record changed but the dialog stays open. */
    readonly onChanged: () => void;
    readonly onSaved: () => void;
    /** Disabled or enabled: the dialog closes, since the row may leave the current filter. */
    readonly onDisabledChanged: (message: string) => void;
    readonly onClose: () => void;
}

/** Renders the title, body and actions of a Dialog; the caller owns the <Dialog>. */
function StaffDetail({
    member,
    groups,
    skillsets,
    payrollCategories,
    isSelf,
    onChanged,
    onSaved,
    onDisabledChanged,
    onClose,
}: StaffDetailProps): React.JSX.Element {
    const [form, setForm] = useState<Record<string, string>>({});
    const [busy, setBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [confirmDisable, setConfirmDisable] = useState(false);
    const [flags, setFlags] = useState<Partial<Record<DisplayFlag, boolean>>>({});

    useEffect(() => {
        setForm({});
        setFlags({});
        setError(null);
        setConfirmDisable(false);
    }, [member]);

    const value = (key: keyof StaffMember): string => {
        const override = form[key];
        if (override !== undefined) {
            return override;
        }
        const v = member[key];
        return v === null || v === undefined ? '' : String(v);
    };

    const set = (key: keyof StaffMember) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
    };

    const savedCategories = useMemo(() => [...(member.payrollCategoryIds ?? [])].sort((a, b) => a - b), [member]);
    const [categoryIds, setCategoryIds] = useState<readonly number[]>(savedCategories);
    useEffect(() => {
        setCategoryIds(savedCategories);
    }, [savedCategories]);
    const categoriesDirty = categoryIds.join(',') !== savedCategories.join(',');

    const flag = (key: DisplayFlag): boolean => flags[key] ?? member[key] ?? false;

    const dirty = Object.keys(form).length > 0 || Object.keys(flags).length > 0 || categoriesDirty;

    function save(): void {
        setBusy(true);
        setSaving(true);
        setError(null);
        const body: Record<string, unknown> = { ...form, ...flags };
        for (const n of ['staffGroupId', 'staffTitleId', 'hoursPerWeek', 'baseHours', 'standardHours', 'rdoAccrual']) {
            if (typeof body[n] === 'string') {
                body[n] = body[n] === '' ? null : Number(body[n]);
            }
        }
        // Separate calls: the record PATCH refuses an empty body, and categories are their own table.
        const requests: Promise<unknown>[] = [];
        if (Object.keys(body).length > 0) {
            requests.push(
                apiFetch<StaffMember>(`/staff-admin/${String(member.staffId)}`, {
                    method: 'PATCH',
                    body: JSON.stringify(body),
                }),
            );
        }
        if (categoriesDirty) {
            requests.push(
                apiFetch<unknown>(`/staff-admin/${String(member.staffId)}/payroll-categories`, {
                    method: 'PUT',
                    body: JSON.stringify({ categoryIds }),
                }),
            );
        }
        Promise.all(requests)
            .then(onSaved)
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not save');
            })
            .finally(() => {
                setBusy(false);
                setSaving(false);
            });
    }

    function setDisabled(disabled: boolean): void {
        setBusy(true);
        setError(null);
        apiFetch<{ message: string }>(`/staff-admin/${String(member.staffId)}/disabled`, {
            method: 'POST',
            body: JSON.stringify({ disabled }),
        })
            .then((r) => {
                onDisabledChanged(r.message);
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : disabled ? 'Could not disable' : 'Could not enable');
            })
            .finally(() => {
                setBusy(false);
            });
    }

    function authAction(path: string, body?: unknown): void {
        setBusy(true);
        setError(null);
        setNotice(null);
        apiFetch<{ message: string }>(`/staff-admin/${String(member.staffId)}/${path}`, {
            method: 'POST',
            body: JSON.stringify(body ?? {}),
        })
            .then((r) => {
                setNotice(r.message);
                onChanged();
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'That did not work');
            })
            .finally(() => {
                setBusy(false);
            });
    }

    const badge = AUTH_LABEL[member.authStatus];
    const hasEmail = member.emailAddress !== null && member.emailAddress !== '';

    return (
        <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                Edit Staff
                <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                    {member.staffName}
                </Typography>
                <Chip size="small" variant="outlined" color={badge.colour} label={badge.label} />
                {member.isActive ? null : <Chip size="small" label="Disabled" color="error" variant="outlined" />}
                {isSelf ? <Chip size="small" label="You" variant="outlined" /> : null}
                <Box sx={{ flex: 1 }} />
                <IconButton onClick={onClose} size="small" disabled={busy} aria-label="Close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {error !== null ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : null}
                {notice !== null ? (
                    <Alert
                        severity="success"
                        sx={{ mb: 2 }}
                        onClose={() => {
                            setNotice(null);
                        }}
                    >
                        {notice}
                    </Alert>
                ) : null}

                <Stack gap={2.5}>
                    {/* ─── Details ─────────────────────────────────────────────── */}
                    <PanelCard>
                        <SectionLabel>Details</SectionLabel>
                        <Stack gap={1.5}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                <TextField
                                    label="Name"
                                    size="small"
                                    fullWidth
                                    value={value('staffName')}
                                    onChange={set('staffName')}
                                />
                                <TextField
                                    label="Email"
                                    size="small"
                                    fullWidth
                                    value={value('emailAddress')}
                                    onChange={set('emailAddress')}
                                    helperText={
                                        member.authUserId === null
                                            ? 'Where the invite will go'
                                            : 'Changing this does not change their sign-in address'
                                    }
                                />
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                <TextField
                                    label="Phone"
                                    size="small"
                                    fullWidth
                                    value={value('phone')}
                                    onChange={set('phone')}
                                />
                                <TextField
                                    select
                                    label="Role"
                                    size="small"
                                    fullWidth
                                    value={value('staffGroupId')}
                                    onChange={set('staffGroupId')}
                                    disabled={isSelf}
                                    helperText={isSelf ? 'You cannot change your own role' : ' '}
                                >
                                    <MenuItem value="">—</MenuItem>
                                    {groups.map((g) => (
                                        <MenuItem key={g.id} value={String(g.id)}>
                                            {g.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                {/* The legacy column that was supposed to hold this
                                    (`calandar_skillset`) is empty on 58 of 59 staff; the
                                    skillset was in a varchar of title names. */}
                                <TextField
                                    select
                                    label="Skillset"
                                    size="small"
                                    fullWidth
                                    value={value('staffTitleId')}
                                    onChange={set('staffTitleId')}
                                    helperText="Sets their colour on the calendar"
                                >
                                    <MenuItem value="">—</MenuItem>
                                    {skillsets.map((k) => (
                                        <MenuItem key={k.id} value={String(k.id)}>
                                            {k.title}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    label="Start date"
                                    type="date"
                                    size="small"
                                    fullWidth
                                    value={value('startDate')}
                                    onChange={set('startDate')}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                />
                            </Stack>
                        </Stack>
                    </PanelCard>

                    {/* ─── Timesheet & mobile calendar ─────────────────────────── */}
                    <PanelCard>
                        <SectionLabel>Timesheet &amp; mobile calendar</SectionLabel>
                        <Stack>
                            {DISPLAY_FLAGS.map((f) => {
                                const checked = f.inverted === true ? !flag(f.key) : flag(f.key);
                                return (
                                    <FormControlLabel
                                        key={f.key}
                                        sx={{ alignItems: 'flex-start', '& + &': { mt: 0.5 } }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                sx={{ pt: 0.25 }}
                                                checked={checked}
                                                onChange={(e) => {
                                                    const next = f.inverted === true ? !e.target.checked : e.target.checked;
                                                    setFlags((prev) => {
                                                        const rest = { ...prev };
                                                        delete rest[f.key];
                                                        // Back to the saved value: no longer a change.
                                                        return next === (member[f.key] ?? false) ? rest : { ...rest, [f.key]: next };
                                                    });
                                                }}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body2">{f.label}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {f.hint}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                );
                            })}
                        </Stack>
                    </PanelCard>

                    {/* ─── Sign-in ─────────────────────────────────────────────── */}
                    <PanelCard>
                        <SectionLabel>Sign-in</SectionLabel>

                        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                            {member.authStatus === 'no_account' ? (
                                <Tooltip title={hasEmail ? '' : 'Add an email address first'}>
                                    <span>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<MailIcon />}
                                            disabled={busy || !hasEmail}
                                            onClick={() => {
                                                authAction('invite');
                                            }}
                                        >
                                            Send invite
                                        </Button>
                                    </span>
                                </Tooltip>
                            ) : (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<LockResetIcon />}
                                    disabled={busy || !hasEmail}
                                    onClick={() => {
                                        authAction('reset-password');
                                    }}
                                >
                                    Send password reset
                                </Button>
                            )}

                            {member.authUserId !== null ? (
                                member.accessRevokedAt === null ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        startIcon={<BlockIcon />}
                                        disabled={busy || isSelf}
                                        onClick={() => {
                                            authAction('access', { revoked: true });
                                        }}
                                    >
                                        Revoke sign-in
                                    </Button>
                                ) : (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        startIcon={<CheckCircleIcon />}
                                        disabled={busy}
                                        onClick={() => {
                                            authAction('access', { revoked: false });
                                        }}
                                    >
                                        Restore sign-in
                                    </Button>
                                )
                            ) : null}
                        </Stack>

                        <Stack gap={0.5}>
                            <Detail label="Account" value={member.authUserId ?? 'Not created yet'} mono />
                            <Detail
                                label="Invited"
                                value={member.invitedAt === null ? 'Never' : formatShort(member.invitedAt.slice(0, 10))}
                            />
                            <Detail
                                label="Last signed in"
                                value={
                                    member.lastLoginAt === null
                                        ? 'Never'
                                        : formatShort(member.lastLoginAt.slice(0, 10))
                                }
                            />
                        </Stack>

                        <Alert severity="info" sx={{ mt: 1.5, fontSize: '0.76rem' }}>
                            For security, passwords can&apos;t be viewed or changed here. If someone has
                            forgotten theirs, send them a password reset email and they can choose a new one.
                        </Alert>
                    </PanelCard>

                    {/* ─── Payroll categories ──────────────────────────────────── */}
                    <PanelCard>
                        <SectionLabel>Payroll categories</SectionLabel>
                        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 1 }}>
                            The rows on their Timesheet payroll and the lines sent to Xero. Lunch lets a lunch break be
                            recorded on their timesheet.
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                            }}
                        >
                            {payrollCategories.map((c) => (
                                <FormControlLabel
                                    key={c.id}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={categoryIds.includes(c.id)}
                                            onChange={(e) => {
                                                setCategoryIds((ids) =>
                                                    (e.target.checked ? [...ids, c.id] : ids.filter((id) => id !== c.id)).sort(
                                                        (a, b) => a - b,
                                                    ),
                                                );
                                            }}
                                        />
                                    }
                                    label={c.name}
                                />
                            ))}
                        </Box>
                    </PanelCard>

                    {/* ─── Pay settings ────────────────────────────────────────── */}
                    <PanelCard>
                        <SectionLabel>Pay settings</SectionLabel>
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                            <PayField
                                label="Hours per week"
                                hint="Their contracted hours for a normal week. For example, 38."
                                value={value('hoursPerWeek')}
                                onChange={set('hoursPerWeek')}
                            />
                            <PayField
                                label="Base hours"
                                hint="How many hours they must work in a day before overtime can start. For example, 8."
                                value={value('baseHours')}
                                onChange={set('baseHours')}
                            />
                            <PayField
                                label="Standard hours"
                                hint="Their ordinary paid hours for a normal day. For example, 7.6."
                                value={value('standardHours')}
                                onChange={set('standardHours')}
                            />
                            <PayField
                                label="RDO accrual"
                                hint="Hours set aside toward a rostered day off for each full day worked. For example, 0.4."
                                value={value('rdoAccrual')}
                                onChange={set('rdoAccrual')}
                            />
                        </Stack>
                    </PanelCard>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                {!member.isActive ? (
                    <Button
                        color="success"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => {
                            setDisabled(false);
                        }}
                        disabled={busy}
                        sx={{ mr: 'auto' }}
                    >
                        Enable Staff
                    </Button>
                ) : confirmDisable ? (
                    <Stack direction="row" alignItems="center" gap={1} sx={{ mr: 'auto' }}>
                        <Typography sx={{ fontSize: '0.8rem', color: 'error.main' }}>
                            Disable {member.staffName}? They won&apos;t be able to sign in until enabled again.
                        </Typography>
                        <Button
                            size="small"
                            color="error"
                            variant="contained"
                            onClick={() => {
                                setDisabled(true);
                            }}
                            disabled={busy}
                        >
                            Disable
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setConfirmDisable(false);
                            }}
                        >
                            Keep
                        </Button>
                    </Stack>
                ) : (
                    <Button
                        color="error"
                        startIcon={<BlockIcon />}
                        onClick={() => {
                            setConfirmDisable(true);
                        }}
                        disabled={busy || isSelf}
                        sx={{ mr: 'auto' }}
                    >
                        Disable Staff
                    </Button>
                )}
                <Button onClick={onClose} disabled={busy}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={save}
                    disabled={!dirty || busy}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                    {saving ? 'Saving...' : 'Update Staff'}
                </Button>
            </DialogActions>
        </>
    );
}

/** A pay-settings field with an info icon explaining what to enter. */
function PayField({
    label,
    hint,
    value,
    onChange,
}: {
    label: string;
    hint: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}): React.JSX.Element {
    return (
        <TextField
            label={label}
            size="small"
            fullWidth
            value={value}
            onChange={onChange}
            slotProps={{
                htmlInput: { inputMode: 'decimal' },
                input: {
                    endAdornment: (
                        <InputAdornment position="end">
                            <Tooltip title={hint} arrow>
                                <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                        </InputAdornment>
                    ),
                },
            }}
        />
    );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }): React.JSX.Element {
    return (
        <Stack direction="row" gap={1.5}>
            <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary', minWidth: 120 }}>
                {label}
            </Typography>
            <Typography
                sx={{ fontSize: '0.76rem', fontFamily: mono === true ? 'monospace' : undefined }}
            >
                {value}
            </Typography>
        </Stack>
    );
}
